/**
 * Provider Configuration
 * 
 * Defines provider-specific configurations including endpoints and authentication methods.
 */

import { AIProvider, AuthMethod, ProviderConfig } from '../AIProvider.types';

/**
 * Provider endpoints
 */
export const PROVIDER_ENDPOINTS = {
  [AIProvider.OPENAI]: 'https://api.openai.com/v1/chat/completions',
  [AIProvider.ANTHROPIC]: 'https://api.anthropic.com/v1/messages',
  [AIProvider.GOOGLE]: 'https://generativelanguage.googleapis.com/v1beta/models',
  [AIProvider.XAI]: 'https://api.x.ai/v1/chat/completions'
};

/**
 * Provider authentication methods
 */
export const PROVIDER_AUTH_METHODS: Record<AIProvider, AuthMethod> = {
  [AIProvider.OPENAI]: 'bearer',
  [AIProvider.ANTHROPIC]: 'x-api-key',
  [AIProvider.GOOGLE]: 'query-param',
  [AIProvider.XAI]: 'bearer'
};

/**
 * Forge resolver function names for each provider
 */
export const PROVIDER_TOKEN_RESOLVERS: Record<AIProvider, string> = {
  [AIProvider.OPENAI]: 'getOpenApiToken',
  [AIProvider.ANTHROPIC]: 'getAnthropicApiToken',
  [AIProvider.GOOGLE]: 'getGoogleApiToken',
  [AIProvider.XAI]: 'getXaiApiToken'
};

/**
 * Complete provider configurations
 */
export const PROVIDER_CONFIGS: Record<AIProvider, ProviderConfig> = {
  [AIProvider.OPENAI]: {
    endpoint: PROVIDER_ENDPOINTS[AIProvider.OPENAI],
    authMethod: PROVIDER_AUTH_METHODS[AIProvider.OPENAI],
    tokenResolver: PROVIDER_TOKEN_RESOLVERS[AIProvider.OPENAI]
  },
  [AIProvider.ANTHROPIC]: {
    endpoint: PROVIDER_ENDPOINTS[AIProvider.ANTHROPIC],
    authMethod: PROVIDER_AUTH_METHODS[AIProvider.ANTHROPIC],
    tokenResolver: PROVIDER_TOKEN_RESOLVERS[AIProvider.ANTHROPIC]
  },
  [AIProvider.GOOGLE]: {
    endpoint: PROVIDER_ENDPOINTS[AIProvider.GOOGLE],
    authMethod: PROVIDER_AUTH_METHODS[AIProvider.GOOGLE],
    tokenResolver: PROVIDER_TOKEN_RESOLVERS[AIProvider.GOOGLE]
  },
  [AIProvider.XAI]: {
    endpoint: PROVIDER_ENDPOINTS[AIProvider.XAI],
    authMethod: PROVIDER_AUTH_METHODS[AIProvider.XAI],
    tokenResolver: PROVIDER_TOKEN_RESOLVERS[AIProvider.XAI]
  }
};

/**
 * Get provider configuration by provider type
 */
export const getProviderConfig = (provider: AIProvider): ProviderConfig => {
  return PROVIDER_CONFIGS[provider];
};

/**
 * Get provider endpoint by provider type
 */
export const getProviderEndpoint = (provider: AIProvider): string => {
  return PROVIDER_ENDPOINTS[provider];
};

/**
 * Anthropic-specific configuration
 */
export const ANTHROPIC_CONFIG = {
  /** API version header required by Anthropic */
  apiVersion: '2023-06-01',
  /** Header name for API version */
  apiVersionHeader: 'anthropic-version',
  /** Beta header to enable structured outputs */
  structuredOutputsHeader: 'anthropic-beta',
  /** Beta header value for structured outputs */
  structuredOutputsBeta: 'structured-outputs-2025-11-13',
  /** Default max tokens for Anthropic requests */
  defaultMaxTokens: 4096
};

/**
 * Google-specific configuration
 */
export const GOOGLE_CONFIG = {
  /** API version for Google Generative AI */
  apiVersion: 'v1beta',
  /** Default safety settings threshold */
  safetyThreshold: 'BLOCK_ONLY_HIGH'
};

/**
 * xAI-specific configuration
 */
export const XAI_CONFIG = {
  /** API version for xAI */
  apiVersion: 'v1'
};
